import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { AccountDeletionService } from '../../services/account-deletion.service';
import { AuthService } from '../../services/auth.service';
import { FirebaseTSAuth } from 'firebasets/firebasetsAuth/firebaseTSAuth';
import { Location } from '@angular/common';

@Component({
  selector: 'app-account-deletion',
  templateUrl: './account-deletion.component.html',
  styleUrls: ['./account-deletion.component.css']
})
export class AccountDeletionComponent implements OnInit {
  deletionForm: FormGroup;
  isSubmitting = false;
  hasSubmitted = false;
  requestId: string | null = null;
  auth = new FirebaseTSAuth();
  isAuthenticated = false;
  authError = '';
  
  // Options pour les raisons de suppression
  deletionReasons = [
    { value: 'privacy', label: 'Préoccupations concernant la confidentialité' },
    { value: 'unused', label: 'Je n\'utilise plus cette application' },
    { value: 'unsatisfied', label: 'Je ne suis pas satisfait du service' },
    { value: 'harassment', label: 'Harcèlement ou abus' },
    { value: 'other', label: 'Autre raison' }
  ];

  constructor(
    private fb: FormBuilder,
    private snackBar: MatSnackBar,
    private accountDeletionService: AccountDeletionService,
    private authService: AuthService,
    private location: Location
  ) {
    this.createForm();
    
    // Vérifier si l'utilisateur est déjà connecté
    const currentUser = this.auth.getAuth().currentUser;
    if (currentUser) {
      this.isAuthenticated = true;
    }
  }

  createForm(): void {
    this.deletionForm = this.fb.group({
      // Infos d'authentification (visible uniquement si non connecté)
      email: ['', [Validators.email]],
      password: ['', [Validators.minLength(6)]],
      phoneNumber: ['', [Validators.pattern(/^\+?[0-9]{10,15}$/)]],
      authMethod: ['email', Validators.required], // 'email' ou 'phone'
      
      // Raison de suppression
      reason: ['', Validators.required],
      otherReason: [''],
      contactEmail: ['', [Validators.email]],
      confirmation: [false, Validators.requiredTrue]
    });
  }

  ngOnInit(): void {
    // Désactiver/activer le champ 'autre raison' en fonction de la sélection
    this.deletionForm.get('reason')?.valueChanges.subscribe(reason => {
      const otherReasonControl = this.deletionForm.get('otherReason');
      if (reason === 'other') {
        otherReasonControl?.setValidators([Validators.required]);
      } else {
        otherReasonControl?.clearValidators();
      }
      otherReasonControl?.updateValueAndValidity();
    });

    // Mettre à jour la validation en fonction de la méthode d'authentification
    this.deletionForm.get('authMethod')?.valueChanges.subscribe(method => {
      const emailControl = this.deletionForm.get('email');
      const phoneControl = this.deletionForm.get('phoneNumber');
      const passwordControl = this.deletionForm.get('password');
      
      if (method === 'email') {
        emailControl?.setValidators([Validators.required, Validators.email]);
        phoneControl?.clearValidators();
        passwordControl?.setValidators([Validators.required, Validators.minLength(6)]);
      } else {
        emailControl?.clearValidators(); 
        phoneControl?.setValidators([Validators.required, Validators.pattern(/^\+?[0-9]{10,15}$/)]);
        passwordControl?.setValidators([Validators.required, Validators.minLength(6)]);
      }
      
      emailControl?.updateValueAndValidity();
      phoneControl?.updateValueAndValidity();
      passwordControl?.updateValueAndValidity();
    });
  }

  /**
   * Méthode pour obtenir l'URL publique de cette page
   */
  getPublicUrl(): string {
    const baseUrl = window.location.origin;
    return `${baseUrl}/delete-account`;
  }

  /**
   * Méthode pour authentifier l'utilisateur
   */
  async authenticate(): Promise<boolean> {
    if (this.isAuthenticated) {
      return true;
    }

    const authMethod = this.deletionForm.get('authMethod')?.value;
    const password = this.deletionForm.get('password')?.value;
    
    try {
      if (authMethod === 'email') {
        const email = this.deletionForm.get('email')?.value;
        await this.auth.signInWith({
          email,
          password,
          onComplete: (userCredential) => {
            this.isAuthenticated = true;
            return true;
          },
          onFail: (error) => {
            this.authError = 'Email ou mot de passe incorrect';
            return false;
          }
        });
      } else {
        // Authentification par téléphone
        const phoneNumber = this.deletionForm.get('phoneNumber')?.value;
        
        try {
          await this.authService.signInWithEmailAndPassword('uid', password, phoneNumber);
          this.isAuthenticated = true;
          return true;
        } catch (error) {
          this.authError = 'Numéro de téléphone ou mot de passe incorrect';
          return false;
        }
      }
    } catch (error) {
      this.authError = 'Erreur d\'authentification. Veuillez réessayer.';
      return false;
    }
    
    return this.isAuthenticated;
  }

  /**
   * Méthode pour gérer la soumission du formulaire
   */
  async onSubmit(): Promise<void> {
    if (!this.deletionForm.valid) {
      return;
    }

    this.isSubmitting = true;
    this.authError = '';

    try {
      // Étape 1: Authentifier l'utilisateur si pas déjà connecté
      if (!this.isAuthenticated) {
        const authenticated = await this.authenticate();
        if (!authenticated) {
          this.isSubmitting = false;
          return;
        }
      }

      // À ce stade, l'utilisateur est authentifié
      const currentUser = this.auth.getAuth().currentUser;
      if (!currentUser) {
        this.snackBar.open('Échec de l\'authentification', 'Fermer', { duration: 5000 });
        this.isSubmitting = false;
        return;
      }

      const userId = currentUser.uid;
      
      // Vérifier si l'utilisateur a déjà une demande en cours
      const hasActiveRequest = await this.accountDeletionService.hasActiveDeletionRequest(userId);
      
      if (hasActiveRequest) {
        this.snackBar.open('Vous avez déjà une demande de suppression en cours de traitement', 'Fermer', { duration: 5000 });
        this.isSubmitting = false;
        return;
      }

      // Récupérer les valeurs du formulaire
      const formValues = this.deletionForm.value;
      
      // Formater la raison
      let reason = formValues.reason;
      if (reason === 'other' && formValues.otherReason) {
        reason = `Autre: ${formValues.otherReason}`;
      }

      // Créer la demande de suppression
      this.requestId = await this.accountDeletionService.createDeletionRequest(
        userId,
        reason,
        formValues.contactEmail || ''
      );

      this.hasSubmitted = true;
      this.snackBar.open('Votre demande de suppression a été soumise avec succès', 'Fermer', { duration: 5000 });
    } catch (error) {
      console.error('Erreur lors de la soumission de la demande:', error);
      this.snackBar.open('Une erreur est survenue lors de la soumission. Veuillez réessayer.', 'Fermer', { duration: 5000 });
    } finally {
      this.isSubmitting = false;
    }
  }
}
